import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { useAuth } from '@/contexts/AuthContext';
import { postAttribution } from '@/lib/attribution';
import { redeemPromoCode } from '@/lib/redeemPromo';
import {
  takeStoredCode,
  setPromoInFlight,
  isPromoInFlight,
  isPromoPending,
  usePromoPending,
  captureCallbackParams,
  takeSignInMethod,
} from '@/lib/pendingPromo';
import { LANDING_ROUTE, ONBOARDING_ROUTE, getPostLoginRoute } from '@/lib/landingRoute';
import { isCreatorOnboardingCode, setCreatorOnboardingFlag } from '@/lib/creatorOnboarding';
import { isTeacherOnboardingCode, setTeacherOnboardingFlag } from '@/lib/teacherOnboarding';

// Work that must happen once, right after any sign-in, whichever page is on
// screen when the session appears. Mounted once in App.jsx.
//
// It used to live in Login.jsx's effect. On a real hostname PublicEntryRoute
// swaps Login for a spinner and a redirect the moment a session exists, so that
// effect never ran: codes typed on the landing were never redeemed, and email
// sign-ins lost their referral and affiliate attribution the same way.
//
// Once only: each stored code is taken and cleared before its request goes
// out, and the promo request is guarded by an in-flight flag, so StrictMode,
// a second auth event or a re-render cannot send it twice.

// Pages a just-signed-in person is sitting on. Only from these does the
// redeemer move them on; anywhere else (mid onboarding) it redeems quietly.
const SIGN_IN_PATHS = new Set(['/', '/login', '/auth/callback']);

// If a route change never commits (a chunk that fails to load), let the
// guards take over after this long instead of spinning forever. Long enough
// for a lazy page on slow school wifi.
const RELEASE_FALLBACK_MS = 15000;

const takeSession = (key) => {
  try {
    const v = sessionStorage.getItem(key);
    if (v) sessionStorage.removeItem(key);
    return v || null;
  } catch {
    return null;
  }
};

const attribute = (path, body, event, code, method) => {
  postAttribution(path, body)
    .then((result) => {
      try {
        posthog?.capture(result?.ok ? `${event}_attributed` : `${event}_attribute_failed`, { code, method, reason: result?.error });
      } catch { /* noop */ }
    })
    .catch((err) => {
      try { posthog?.capture(`${event}_attribute_error`, { code, method, error: String(err) }); } catch { /* noop */ }
    });
};

const PendingCodeRedeemer = () => {
  const {
    user,
    loading,
    subscriptionLoading,
    hasActiveSubscription,
    hasCompletedOnboarding,
    checkSubscriptionStatus,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const promoPending = usePromoPending();

  const email = user?.email || null;

  // An email link opens a new tab: take the code and method it carried in its
  // query before anything below looks for them.
  useLayoutEffect(() => {
    captureCallbackParams(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // How this sign-in started, read once per signed-in email.
  const methodRef = useRef({ email: null, method: null });
  const signInMethod = () => {
    if (methodRef.current.email !== email) {
      methodRef.current = { email, method: takeSignInMethod(pathRef.current) };
    }
    return methodRef.current.method;
  };

  // The in-flight hold is released only once the route change has committed
  // and left the sign-in pages. Production wraps navigations in a transition
  // and /timo is lazy, so "next tick" is too early: PublicEntryRoute, still on
  // /login, would render its own redirect and overwrite this one.
  const releaseOnLeave = useRef(null);
  const release = () => {
    clearTimeout(releaseOnLeave.current);
    releaseOnLeave.current = null;
    setPromoInFlight(false);
  };
  useEffect(() => {
    if (releaseOnLeave.current && !SIGN_IN_PATHS.has(location.pathname)) release();
  }, [location.pathname]);
  useEffect(() => () => clearTimeout(releaseOnLeave.current), []);

  const finish = (to) => {
    if (!SIGN_IN_PATHS.has(pathRef.current)) {
      release();
      return;
    }
    clearTimeout(releaseOnLeave.current);
    releaseOnLeave.current = setTimeout(release, RELEASE_FALLBACK_MS);
    navigate(to, { replace: true });
  };

  // Referral and affiliate: fire and forget, as soon as there is a session.
  useEffect(() => {
    if (!email || loading) return;
    const method = signInMethod(); // taken on every sign-in so a hint never goes stale
    const refCode = takeSession('ttt_ref_code');
    const affCode = takeSession('ttt_aff_code');
    if (refCode) attribute('/api/referral/attribute', { refCode }, 'referral', refCode, method);
    if (affCode) attribute('/api/affiliate/attribute', { affiliateCode: affCode }, 'affiliate', affCode, method);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, loading]);

  // Promo code: wait for the subscription check so the fallback route is right.
  // promoPending is a dep so a code saved while already signed in is redeemed
  // straight away instead of leaving / or /login on a spinner.
  useEffect(() => {
    if (!email || loading || subscriptionLoading) return;
    if (isPromoInFlight() || !isPromoPending()) return;

    const code = takeStoredCode();
    if (!code) return;
    const method = signInMethod();

    // Teacher code: not a promo. It only opens the teacher onboarding track.
    // Unreachable today: TEACHER is not in PROMO_CODE_DAYS, so no code box stores it. G decides the teacher path.
    if (isTeacherOnboardingCode(code)) {
      setPromoInFlight(true);
      if (!hasCompletedOnboarding) {
        setTeacherOnboardingFlag();
        finish(ONBOARDING_ROUTE);
      } else {
        finish(LANDING_ROUTE);
      }
      return;
    }

    setPromoInFlight(true);
    const fallback = getPostLoginRoute({ user, hasActiveSubscription, hasCompletedOnboarding });
    const onboarded = hasCompletedOnboarding;
    (async () => {
      let r = { ok: false };
      try {
        r = await redeemPromoCode({ email, code, checkSubscriptionStatus, method });
      } finally {
        if (r.ok) {
          // Creator codes walk the short creator onboarding first, as before.
          if (isCreatorOnboardingCode(code) && !onboarded) {
            setCreatorOnboardingFlag();
            finish(ONBOARDING_ROUTE);
          } else {
            finish(LANDING_ROUTE);
          }
        } else {
          finish(fallback);
        }
      }
    })();
    // user is read for the fallback route only; email is the identity that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, loading, subscriptionLoading, promoPending]);

  return null;
};

export default PendingCodeRedeemer;
